import { ChefHat, Loader2 } from 'lucide-react';

import CompactSelectField from '../../../../components/Forms/Input/CompactSelectField';
import SelectField from '../../../../components/Forms/Input/SelectField';

import { IBranchDropdown } from '../../../../types/branches/branchInterfaces';
import { RestaurantKitchenOrderStatus } from '../../../../types/restaurant/RestaurantKitchenOrderTypes';
import { RestaurantPreparationStationDTO } from '../../../../types/restaurant/RestaurantPreparationStationTypes';

export const kitchenStatusOptions: Array<{
  value: RestaurantKitchenOrderStatus | '';
  label: string;
}> = [
  { value: '', label: 'Todas' },
  { value: 'PENDING', label: 'Pendientes' },
  { value: 'IN_PREPARATION', label: 'Preparando' },
  { value: 'READY', label: 'Listas' },
  { value: 'CANCELLED', label: 'Canceladas' },
];

interface KitchenHeaderProps {
  branches: IBranchDropdown[];
  branchId: number | '';
  loadingBranches: boolean;
  canPickBranch: boolean;
  stations: RestaurantPreparationStationDTO[];
  stationId: number | '';
  status: RestaurantKitchenOrderStatus | '';
  compact?: boolean;
  onBranchChange: (branchId: number) => void;
  onStationChange: (stationId: number | '') => void;
  onStatusChange: (status: RestaurantKitchenOrderStatus | '') => void;
}

const KitchenHeader = ({
  branches,
  branchId,
  loadingBranches,
  canPickBranch,
  stations,
  stationId,
  status,
  compact = false,
  onBranchChange,
  onStationChange,
  onStatusChange,
}: KitchenHeaderProps) => {
  const branchOptions = branches.map((branch) => ({
    value: branch.id,
    label: branch.name,
  }));
  const stationOptions = [
    { value: '', label: 'Todas las estaciones' },
    ...stations.map((station) => ({ value: station.id, label: station.name })),
  ];

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-3 border-b border-stroke bg-white px-4 py-3 dark:border-strokedark dark:bg-boxdark">
        <span className="inline-flex items-center gap-2 text-sm font-bold text-black dark:text-white">
          <ChefHat className="h-4 w-4 text-primary" />
          Cocina
        </span>

        {loadingBranches ? (
          <span className="inline-flex items-center gap-2 text-xs text-bodydark2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            Cargando...
          </span>
        ) : (
          <div className="flex flex-1 flex-wrap items-center gap-2">
            {canPickBranch && (
              <CompactSelectField
                name="kitchenBranch"
                value={branchId}
                iconKey="building"
                placeholder="Sin sucursales"
                options={branchOptions}
                onChange={(event) => onBranchChange(Number(event.target.value))}
                className="min-w-[150px]"
              />
            )}
            <CompactSelectField
              name="kitchenStation"
              value={stationId}
              iconKey="store"
              placeholder="Todas las estaciones"
              options={stationOptions}
              onChange={(event) =>
                onStationChange(
                  event.target.value ? Number(event.target.value) : ''
                )
              }
              className="min-w-[150px]"
            />
            <CompactSelectField
              name="kitchenStatus"
              value={status}
              iconKey="status"
              options={kitchenStatusOptions}
              onChange={(event) =>
                onStatusChange(
                  event.target.value as RestaurantKitchenOrderStatus | ''
                )
              }
              className="min-w-[140px]"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border-b border-stroke bg-white px-5 py-5 dark:border-strokedark dark:bg-boxdark sm:px-7">
      <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex items-start gap-3">
          <span className="rounded-xl border border-primary/15 bg-primary/10 p-3 text-primary">
            <ChefHat className="h-6 w-6" />
          </span>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Restaurante
            </p>

            <h1 className="mt-1 text-2xl font-bold text-black dark:text-white">
              Tablero de cocina
            </h1>

            <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
              Prepare comandas completas o avance cada plato por estación.
            </p>
          </div>
        </div>

        <div
          className={`grid w-full gap-3 xl:max-w-3xl ${
            canPickBranch ? 'md:grid-cols-3' : 'md:grid-cols-2'
          }`}
        >
          {loadingBranches ? (
            <div className="flex h-[74px] items-center justify-center gap-2 text-sm text-bodydark2 md:col-span-3">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Cargando sucursales...
            </div>
          ) : (
            <>
              {canPickBranch && (
                <SelectField
                  label="Sucursal"
                  name="kitchenBranch"
                  value={branchId}
                  iconKey="building"
                  placeholder="Sin sucursales"
                  options={branchOptions}
                  onChange={(event) => onBranchChange(Number(event.target.value))}
                />
              )}

              <SelectField
                label="Estación"
                name="kitchenStation"
                value={stationId}
                iconKey="store"
                placeholder="Todas las estaciones"
                options={stationOptions}
                onChange={(event) =>
                  onStationChange(
                    event.target.value ? Number(event.target.value) : ''
                  )
                }
              />

              <SelectField
                label="Estado"
                name="kitchenStatus"
                value={status}
                iconKey="status"
                options={kitchenStatusOptions}
                onChange={(event) =>
                  onStatusChange(
                    event.target.value as RestaurantKitchenOrderStatus | ''
                  )
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KitchenHeader;
